import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import {
  FiFileText, FiTrash2, FiAlertCircle,
  FiThumbsUp, FiThumbsDown, FiCopy, FiRefreshCw,
} from 'react-icons/fi';
import { handleUnauthorized } from '../../utils/apiClient';
import { useToast } from '../../context/ToastContext';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import './PolicyComparison.css';



const getToken = (): string => localStorage.getItem('toolboxToken') || '';

interface FeedbackState {
  text: string;
  rating: boolean | null;
  error: string;
  submitting: boolean;
  success: boolean;
  anchor: { bottom: number; right: number } | null;
}

interface Props {
  onGoToHistory?: () => void;
}

const PolicyComparisonUpload: React.FC<Props> = ({ onGoToHistory }) => {
  const { showSuccess, showWarning, showError } = useToast();

  /* ── files ── */
  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);
  const [drag1, setDrag1] = useState(false);
  const [drag2, setDrag2] = useState(false);
  const [fileErr1, setFileErr1] = useState('');
  const [fileErr2, setFileErr2] = useState('');
  const fileRef1 = useRef<HTMLInputElement>(null);
  const fileRef2 = useRef<HTMLInputElement>(null);

  /* ── balance ── */
  const [insufficientBalance, setInsufficient] = useState(false);

  /* ── streaming response ── */
  const [response, setResponse]         = useState('');
  const [streaming, setStreaming]       = useState(false);
  const [activeAction, setActiveAction] = useState<number | null>(null);
  const [feedbackId, setFeedbackId]     = useState('');
  const ctrl = useRef<AbortController | null>(null);

  /* ── feedback ── */
  const [fb, setFb] = useState<FeedbackState>({
    text: '', rating: null, error: '', submitting: false, success: false, anchor: null,
  });
  const feedbackRef = useRef<HTMLDivElement>(null);

  const fetchBalance = useCallback(() => {
    if (!(process.env.REACT_APP_STRIPEPAYMENTGATEWAY || '')) return;
    axios.get(`${(process.env.REACT_APP_STRIPEPAYMENTGATEWAY || '')}/api/billing/current-balance`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (res.data.success) {
          setInsufficient(res.data.currentBalance / 100 <= 0);
        }
      })
      .catch(() => {});
  }, []);

  // useEffect(() => { fetchBalance(); }, [fetchBalance]);

  /* ── close feedback on outside click ── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) {
        setFb(p => ({ ...p, anchor: null }));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── file helpers ── */
  const acceptFile1 = (f: File) => {
    if (f.type !== 'application/pdf') { setFileErr1('Only PDF files are accepted.'); return; }
    setFileErr1(''); setFile1(f);
  };
  const acceptFile2 = (f: File) => {
    if (f.type !== 'application/pdf') { setFileErr2('Only PDF files are accepted.'); return; }
    setFileErr2(''); setFile2(f);
  };

  /* ── validate before submit ── */
  const validate = (): boolean => {
    if (!file1 && !file2) { showWarning('Please select both policy PDFs for comparison.'); return false; }
    if (!file1) { showWarning('Please select the first policy PDF.'); return false; }
    if (!file2) { showWarning('Please select the second policy PDF.'); return false; }
    return true;
  };

  /* ── build prompt ── */
  const getPrompt = (prompt: number): string => {
    const n1 = file1?.name ?? 'Policy 1';
    const n2 = file2?.name ?? 'Policy 2';
    if (prompt === 2) return `You are a smart insurance quote comparer. Your task is to analyze ${n1} and ${n2} and provide detailed comparison including limits, coverages and premium in a tabular format. Additionally, offer a brief analysis on which policy might be more suitable for someone prioritizing immediate coverage for pre-existing conditions versus cost savings.`;
    if (prompt === 3) return `You are a smart insurance quote comparer. Your task is to analyze ${n1} and ${n2} for limits and provide a comparison of limits in a tabular format.`;
    if (prompt === 4) return `You are a smart insurance quote comparer. Your task is to analyze ${n1} and ${n2} for coverages and provide a comparison of coverages in a tabular format.`;
    return '';
  };

  const getAction = (prompt: number): string => {
    const map: Record<number, string> = {
      2: 'Compare Policies', 3: 'Compare Limits', 4: 'Compare Coverage Codes',
    };
    return map[prompt] ?? '';
  };

  /* ── run ── */
  const run = async (prompt: number) => {
    if (!validate()) return;
    if (ctrl.current) ctrl.current.abort();
    ctrl.current = new AbortController();

    setActiveAction(prompt);
    setResponse('');
    setFeedbackId('');
    setStreaming(false);

    const formData = new FormData();
    if (file1) formData.append('FirstFormFile', file1);
    if (file2) formData.append('SecondFormFile', file2);
    formData.append('Question', getPrompt(prompt));
    formData.append('Action', getAction(prompt));

    const endpoint = 'process_files';

    try {
      await fetchEventSource(`${(process.env.REACT_APP_PolicyAgent_Url      || '')}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
        signal: ctrl.current.signal,
        onopen: async (res: Response) => {
          if (res.ok && res.headers.get('content-type') === 'text/event-stream') return;
          if (res.status === 401) { handleUnauthorized(); return; }
          if (res.status === 403) showWarning('Insufficient credits to process this request.');
          setActiveAction(null); setStreaming(false);
        },
        onmessage: (event: { data: string; event?: string; id?: string; retry?: number }) => {
          try {
            const parsed = JSON.parse(event.data);
            if (parsed.transaction_id) setFeedbackId(parsed.transaction_id);
            if (parsed.response) {
              setStreaming(true);
              setActiveAction(null);
              setResponse(prev => prev + parsed.response);
            } else {
              setResponse(prev => prev + '\n');
            }
          } catch { /* non-JSON event */ }
        },
        onclose: () => {
          setStreaming(false);
          // fetchBalance();
        },
        onerror: () => {
          setStreaming(false);
          setActiveAction(null);
        },
      });
    } catch { /* aborted */ }
  };

  /* ── reset ── */
  const reset = () => {
    if (ctrl.current) ctrl.current.abort();
    setResponse(''); setStreaming(false); setActiveAction(null); setFeedbackId('');
  };

  /* ── clipboard ── */
  const copyToClipboard = async () => {
    try {
      const html = await marked(response) as string;
      const styledHtml = applyTableStyles(html);
      const plain = htmlToPlain(html);
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([styledHtml], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      showSuccess('Copied to clipboard!');
    } catch {
      showError('Failed to copy to clipboard.');
    }
  };

  const applyTableStyles = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('table').forEach(t => {
      t.setAttribute('style', 'border-collapse:collapse;width:100%;font-family:Arial,sans-serif;');
      t.querySelectorAll('th').forEach(th => th.setAttribute('style', 'border:2px solid #e3e3e3;padding:8px 10px;'));
      t.querySelectorAll('td').forEach(td => td.setAttribute('style', 'border:1px solid #e3e3e3;padding:7px 10px;'));
    });
    return div.innerHTML;
  };

  const htmlToPlain = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('br, p, h1, h2, h3, h4, h5, h6').forEach(el => {
      el.parentNode?.insertBefore(document.createTextNode('\n'), el);
    });
    return div.textContent || div.innerText || '';
  };

  /* ── feedback ── */
  const openFeedback = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setFb(p => ({ ...p, anchor: { bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right }, error: '', success: false }));
  };

  const onSaveRatings = useCallback((isLiked: boolean | null, feedbackText: string) => {
    if (!feedbackId) return;
    axios.post(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/OCR/policycomparisonratings`,
      { TransactionId: feedbackId, IsLiked: isLiked, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => {
      showSuccess('Feedback submitted successfully!');
      setFb(p => ({ ...p, anchor: null, text: '', rating: null, submitting: false }));
    }).catch(err => {
      if (err.response?.status === 401) handleUnauthorized();
    });
  }, [feedbackId, showSuccess]);

  const onThumbsUpClick = useCallback(() => {
    const next: boolean | null = fb.rating === true ? null : true;
    setFb(p => ({ ...p, anchor: null }));
    onSaveRatings(next, fb.text);
  }, [fb.rating, fb.text, onSaveRatings]);

  const onThumbsDownClick = useCallback(() => {
    const next: boolean | null = fb.rating === false ? null : false;
    setFb(p => ({ ...p, anchor: null }));
    onSaveRatings(next, fb.text);
  }, [fb.rating, fb.text, onSaveRatings]);

  const onSubmitFeedback = useCallback(() => {
    if (!fb.text.trim()) { setFb(p => ({ ...p, error: 'Please enter your feedback.' })); return; }
    if (fb.text.length > 250) { setFb(p => ({ ...p, error: 'Feedback must be under 250 characters.' })); return; }
    if (!feedbackId) { showError('No transaction to attach feedback to.'); return; }
    setFb(p => ({ ...p, submitting: true, error: '' }));
    onSaveRatings(fb.rating, fb.text);
  }, [fb.text, fb.rating, feedbackId, showError, onSaveRatings]);

  const isLoading = activeAction !== null;

  return (
    <div className="cs-upload-container">
      {insufficientBalance && (
        <div className="pc-insufficient-banner">
          <FiAlertCircle size={15} />
          Insufficient balance. Please top up your account to use Policy Comparison.
        </div>
      )}

      {/* ── File zones ── */}
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>

        {/* File 1 */}
        <div className="pc-file-zone" style={{ flex: 1 }}>
          <div
            className={`cs-dropzone${drag1 ? ' cs-dropzone--dragging' : ''}`}
            onDrop={e => { e.preventDefault(); setDrag1(false); const f = e.dataTransfer.files[0]; if (f) acceptFile1(f); }}
            onDragOver={e => { e.preventDefault(); setDrag1(true); }}
            onDragLeave={() => setDrag1(false)}
          >
            <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
            <p className="cs-dropzone__title">First Policy</p>
            <p className="cs-dropzone__or">Drag & Drop your file here</p>
            <p className="cs-dropzone__or">or</p>
            <button
              type="button"
              className="cs-browse-btn"
              onClick={() => fileRef1.current?.click()}
            >
              Browse Files
            </button>
            <p className="cs-dropzone__types">Allowed file types: <strong>PDF</strong></p>
            {file1 && (
              <div className="cs-file-card" style={{ marginTop: 10, width: '100%' }}>
                <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                <div className="cs-file-card__info">
                  <span className="cs-file-card__name">{file1.name}</span>
                  <span className="cs-file-card__size">{(file1.size / 1024).toFixed(0)} KB</span>
                </div>
                <button className="cs-file-card__delete" onClick={() => { setFile1(null); reset(); }} title="Remove file">
                  <FiTrash2 size={15} />
                </button>
              </div>
            )}
            {fileErr1 && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>{fileErr1}</div>}
            <input ref={fileRef1} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) acceptFile1(f); e.target.value = ''; }} />
          </div>
          <div className="cs-info-banner">
            <FiAlertCircle size={14} />
            <span>
              This tool works best with readable PDFs. Please note that scanned or
              image-based PDFs may not be processed. For scanned documents, consider
              converting them to a readable format using OCR tools before uploading.
            </span>
          </div>
        </div>

        {/* File 2 */}
        <div className="pc-file-zone" style={{ flex: 1 }}>
          <div
            className={`cs-dropzone${drag2 ? ' cs-dropzone--dragging' : ''}`}
            onDrop={e => { e.preventDefault(); setDrag2(false); const f = e.dataTransfer.files[0]; if (f) acceptFile2(f); }}
            onDragOver={e => { e.preventDefault(); setDrag2(true); }}
            onDragLeave={() => setDrag2(false)}
          >
            <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
            <p className="cs-dropzone__title">Second Policy</p>
            <p className="cs-dropzone__or">Drag & Drop your file here</p>
            <p className="cs-dropzone__or">or</p>
            <button
              type="button"
              className="cs-browse-btn"
              onClick={() => fileRef2.current?.click()}
            >
              Browse Files
            </button>
            <p className="cs-dropzone__types">Allowed file types: <strong>PDF</strong></p>
            {file2 && (
              <div className="cs-file-card" style={{ marginTop: 10, width: '100%' }}>
                <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                <div className="cs-file-card__info">
                  <span className="cs-file-card__name">{file2.name}</span>
                  <span className="cs-file-card__size">{(file2.size / 1024).toFixed(0)} KB</span>
                </div>
                <button className="cs-file-card__delete" onClick={() => { setFile2(null); reset(); }} title="Remove file">
                  <FiTrash2 size={15} />
                </button>
              </div>
            )}
            {fileErr2 && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>{fileErr2}</div>}
            <input ref={fileRef2} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) acceptFile2(f); e.target.value = ''; }} />
          </div>
          <div className="cs-info-banner">
            <FiAlertCircle size={14} />
            <span>
              This tool works best with readable PDFs. Please note that scanned or
              image-based PDFs may not be processed. For scanned documents, consider
              converting them to a readable format using OCR tools before uploading.
            </span>
          </div>
        </div>
      </div>

      {/* ── Action buttons ── */}
      <div className="pc-actions">
        <button
          className={`pc-action-btn${activeAction === 3 ? ' pc-action-btn--active' : ''}`}
          disabled={isLoading || insufficientBalance}
          onClick={() => run(3)}
        >
          {activeAction === 3 ? <span className="pc-spinner" /> : null}
          Compare Limits
        </button>
        <button
          className={`pc-action-btn${activeAction === 4 ? ' pc-action-btn--active' : ''}`}
          disabled={isLoading || insufficientBalance}
          onClick={() => run(4)}
        >
          {activeAction === 4 ? <span className="pc-spinner" /> : null}
          Compare Coverage Codes
        </button>
        <button
          className={`pc-action-btn${activeAction === 2 ? ' pc-action-btn--active' : ''}`}
          disabled={isLoading || insufficientBalance}
          onClick={() => run(2)}
        >
          {activeAction === 2 ? <span className="pc-spinner" /> : null}
          Compare Policies
        </button>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
        AI-generated output — verify important information before use.
      </div>

      {/* ── Streaming response ── */}
      {(response || streaming) && (
        <div className="pc-response-card">
          <div className="pc-markdown-body">
            <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
              {response}
            </ReactMarkdown>
            {streaming && <span className="pc-streaming-cursor" />}
          </div>

          {!streaming && (
            <div className="pc-response-card__toolbar">
              <button className="pc-response-card__toolbar-btn" onClick={reset}>
                <FiRefreshCw size={12} /> Try Again
              </button>
              <button className="pc-response-card__toolbar-btn" onClick={copyToClipboard}>
                <FiCopy size={12} /> Copy
              </button>
              {feedbackId && (
                <button className="pc-response-card__toolbar-btn" onClick={openFeedback}>
                  Feedback
                </button>
              )}
              {onGoToHistory && (
                <button className="pc-response-card__toolbar-btn" onClick={onGoToHistory}>
                  View History
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Feedback popup (portalled) ── */}
      {fb.anchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ bottom: fb.anchor.bottom, right: fb.anchor.right, top: 'auto' }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            rows={4}
            placeholder="Enter your feedback here…"
            value={fb.text}
            onChange={e => setFb(p => ({ ...p, text: e.target.value, error: '' }))}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${fb.rating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Helpful"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${fb.rating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Not helpful"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {fb.error && <p className="csfb-error">{fb.error}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={() => setFb(p => ({ ...p, anchor: null }))}>
              Cancel
            </button>
            <button
              className="csfb-btn-submit"
              disabled={fb.submitting}
              onClick={onSubmitFeedback}
            >
              {fb.submitting ? '…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PolicyComparisonUpload;

